<template>
	<!-- TX complete -->
	<div v-if="!txTypeStr" class="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-b from-[#001F13] via-[#003A24] to-[#01321F] backdrop-blur-xl overflow-hidden">
		<div class="relative max-w-xl w-full p-10 mx-4 bg-gradient-to-br from-green-800 via-green-700 to-green-600
		rounded-3xl shadow-2xl transform transition-all duration-700 animate-scaleIn overflow-hidden">
			<!-- Spinning -->
			<div class="absolute inset-0 opacity-20 animate-slowSpin" style="background: conic-gradient(from 90deg at 50% 50%, #2ecc71, #27ae60, #1abc9c, #16a085); filter: blur(40px);"
			></div>

			<div class="relative z-10 flex flex-col items-center">
				<div class="relative w-20 h-20 mb-6">
					<svg class="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
						<circle cx="50" cy="50" r="45" stroke="#a3e635" stroke-width="4" fill="none" stroke-dasharray="283" stroke-dashoffset="283" class="animate-circle-draw"/>
					</svg>
					<svg class="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
						<path d="M30 55 L45 70 L70 40" stroke="#ffffff" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="100" stroke-dashoffset="100" class="animate-check-draw"/>
					</svg>
				</div>

				<h2 class="text-4xl font-archivosemibold text-white mb-6 text-center drop-shadow-lg tracking-wider" >
					Transaction Confirmed
				</h2>

				<p class="text-sm text-green-50 mb-8 text-center px-8 md:px-4 break-all">
					<strong>TXID:</strong> 
					{{ txidStr }}
				</p>

				<div class="flex flex-col sm:flex-row gap-4 w-full">
					<button @click="$emit('viewInExplorer')" class="flex-1 py-3 px-6 bg-white text-green-800 font-archivomedium rounded-full shadow-md hover:shadow-xl transition transform duration-200 hover:scale-105">
						View in Explorer
					</button>
					<button @click="$emit('done')" class="flex-1 py-3 px-6 border border-white text-white font-archivomedium rounded-full transition hover:bg-white hover:text-green-800 transform hover:scale-105 shadow-md duration-300">
						Done
					</button>
				</div>
			</div>
		</div>
	</div>

	<!-- If txTypeStr is non‐empty (WalletConnect flow), show yellow/orange "Signed & Broadcasted" screen -->
	<div v-else class="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-b from-[#3C2600] via-[#603C00] to-[#804C00] backdrop-blur-xl overflow-hidden">
		<div class="relative max-w-xl w-full p-10 mx-4 bg-gradient-to-br from-yellow-700 via-yellow-600 to-yellow-500
		rounded-3xl shadow-2xl transform transition-all duration-700 animate-scaleIn overflow-hidden">
			<!-- Spinning amber/orange blurred background -->
			<div class="absolute inset-0 opacity-20 animate-slowSpin" style="background: conic-gradient(from 90deg at 50% 50%, #f39c12, #e67e22, #d35400,#c0392b); filter: blur(40px);"></div>
			<div class="relative z-10 flex flex-col items-center">
				<div class="relative w-20 h-20 mb-6">
					<svg class="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
						<circle cx="50" cy="50" r="45" stroke="#f1c40f" stroke-width="4" fill="none" stroke-dasharray="283" stroke-dashoffset="283" class="animate-circle-draw"/>
					</svg>
					<svg class="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
						<path d="M30 55 L45 70 L70 40" stroke="#ffffff" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="100" stroke-dashoffset="100" class="animate-check-draw"/>
					</svg>
				</div>

				<h2 class="text-4xl font-archivosemibold text-white mb-6 text-center drop-shadow-lg tracking-wider">
					Transaction Broadcasted
				</h2>

				<p class="text-md md:text-lg text-yellow-50 mb-8 text-center px-8 md:px-4">
					Please revert back to the dApp for final confirmation. This may take up to 30 seconds.
				</p>

				<p v-if="txTypeStr !== ''" class="text-sm text-yellow-50 mb-8 text-center px-8 md:px-4 break-all">
					<strong>TXID:</strong> 
					{{ txidStr }}
				</p>

				<div v-if="txTypeStr === 'eth_sendTransaction'" class="flex flex-col sm:flex-row gap-4 w-full">
					<button @click="$emit('viewInExplorer')" class="flex-1 py-3 px-6 bg-white text-yellow-800 font-archivomedium rounded-full shadow-md hover:shadow-xl transition transform duration-200 hover:scale-105">
						View in Explorer
					</button>
					<button @click="$emit('done')" class="flex-1 py-3 px-6 border border-white text-white font-archivomedium rounded-full transition hover:bg-white hover:text-yellow-800 transform hover:scale-105 shadow-md duration-300">
						Done
					</button>
				</div>
				<div v-else class="flex flex-col sm:flex-row gap-4 w-full">
					<button @click="$emit('done')" class="flex-1 py-3 px-6 border border-white text-white font-archivomedium rounded-full transition hover:bg-white hover:text-yellow-800 transform hover:scale-105 shadow-md duration-300">
						Done
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>

	const props = defineProps({
		txTypeStr: { type: String, required: true },
		txidStr: { type: String, required: true }
	});

	const emit = defineEmits([
		'viewInExplorer',
		'done'
	]);
</script>

<style scoped>
	/* Pop‐in animation */
	@keyframes scaleIn {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
	}
	.animate-scaleIn {
		animation: scaleIn 0.7s ease-out forwards;
	}

	/* Slow‐spin background */
	@keyframes slowSpin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
	}
	.animate-slowSpin {
		animation: slowSpin 20s linear infinite;
	}

	/* Circle draw (stroke‐dashoffset) */
	@keyframes circleDraw {
	to {
		stroke-dashoffset: 0;
	}
	}
	.animate-circle-draw {
		animation: circleDraw 1.2s ease-out forwards;
	}

	/* Checkmark draw */
	@keyframes checkDraw {
	to {
		stroke-dashoffset: 0;
	}
	}
	.animate-check-draw {
		animation: checkDraw 0.8s ease-out forwards;
	}
</style>
